const MAX_VISIBLE_PAGES_THRESHOLD = 9;
const WINDOW_SIZE = 5;
const BOUNDARY_SIZE = 2;

export interface PageItem {
  type: 'page' | 'ellipsis';
  page?: number;
  key: string;
}

const createPageRange = (start: number, end: number): PageItem[] =>
  Array.from({ length: end - start + 1 }, (_, index) => ({
    type: 'page',
    page: start + index,
    key: `page-${start + index}`,
  }));

const createEllipsis = (position: 'start' | 'end'): PageItem => ({
  type: 'ellipsis',
  key: `ellipsis-${position}`,
});

const buildNearStartPages = (totalPages: number): PageItem[] => [
  ...createPageRange(1, BOUNDARY_SIZE + WINDOW_SIZE - 1),
  createEllipsis('end'),
  ...createPageRange(totalPages - BOUNDARY_SIZE + 1, totalPages),
];

const buildNearEndPages = (totalPages: number): PageItem[] => {
  const endBlockSize = BOUNDARY_SIZE + WINDOW_SIZE - 1;
  const endBlockStart = totalPages - endBlockSize + 1;

  return [
    ...createPageRange(1, BOUNDARY_SIZE),
    createEllipsis('start'),
    ...createPageRange(endBlockStart, totalPages),
  ];
};

const buildMiddlePages = (
  windowStart: number,
  windowEnd: number,
  totalPages: number
): PageItem[] => {
  const hasLeftGap = windowStart > BOUNDARY_SIZE + 1;
  const hasRightGap = windowEnd < totalPages - BOUNDARY_SIZE;

  return [
    ...createPageRange(1, BOUNDARY_SIZE),
    ...(hasLeftGap ? [createEllipsis('start')] : []),
    ...createPageRange(windowStart, windowEnd),
    ...(hasRightGap ? [createEllipsis('end')] : []),
    ...createPageRange(totalPages - BOUNDARY_SIZE + 1, totalPages),
  ];
};

export const calculateVisiblePages = (
  currentPage: number,
  totalPages: number
): PageItem[] => {
  if (totalPages <= MAX_VISIBLE_PAGES_THRESHOLD) {
    return createPageRange(1, totalPages);
  }

  const halfWindow = Math.floor(WINDOW_SIZE / 2);
  const windowStart = currentPage - halfWindow;
  const windowEnd = currentPage + halfWindow;

  if (windowStart <= BOUNDARY_SIZE) {
    return buildNearStartPages(totalPages);
  }

  if (windowEnd >= totalPages - BOUNDARY_SIZE + 1) {
    return buildNearEndPages(totalPages);
  }

  return buildMiddlePages(windowStart, windowEnd, totalPages);
};
